import React, { useState } from 'react';
import '../App.css';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

function ResourceSection({ resources, setResources, onResourcesChange, currentGameId }) {
  const { isHost } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [newResource, setNewResource] = useState({ name: '', url: '', type: 'link' });
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleAddResource = async () => {
    if (!newResource.name.trim()) {
      alert('Please provide a resource title');
      return;
    }

    // Require URL for link type resources
    if (newResource.type === 'link' && !newResource.url.trim()) {
      alert('Please provide a URL');
      return;
    }

    setUploading(true);
    try {
      let imageUrl = null;
      let imagePath = null;

      // Always require an image upload
      if (!uploadedImage) {
        alert('Please upload an image for the resource');
        setUploading(false);
        return;
      }

      // Upload image to Supabase Storage
      const fileExt = uploadedImage.name.split('.').pop();
      const fileName = `resources/${Date.now()}_${newResource.name.replace(/\s+/g, '_')}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('design-submissions')
        .upload(fileName, uploadedImage);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('design-submissions')
        .getPublicUrl(fileName);

      imageUrl = publicUrl;
      imagePath = fileName;

      // Save resource record
      // For link types: store external URL in url field, image URL separately
      // For file types: store image URL in url field
      const resourceData = {
        name: newResource.name.trim(),
        type: newResource.type === 'link' ? 'link' : 'file',
        url: newResource.type === 'link' && newResource.url ? newResource.url.trim() : imageUrl,
        file_path: imagePath
      };
      
      // Associate with current game if available
      if (currentGameId) {
        resourceData.game_id = currentGameId;
      }
      
      // Note: For link resources, url = external link, file_path = image path
      // For file resources, url = image URL, file_path = image path

      const { data, error } = await supabase
        .from('resources')
        .insert([resourceData])
        .select()
        .single();

      if (error) throw error;

      // Reset form
      setNewResource({ name: '', url: '', type: 'link' });
      setUploadedImage(null);
      
      // Reload resources from parent to ensure we have the latest data with proper URLs
      if (onResourcesChange) {
        onResourcesChange();
      } else {
        // If no callback, update local state
        setResources([...resources, {
          id: data.id,
          name: data.name,
          type: data.type,
          url: data.url,
          file_path: data.file_path
        }]);
      }
    } catch (error) {
      console.error('Error adding resource:', error);
      alert('Failed to add resource. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setUploadedImage(file);
      // Preview the image
      const reader = new FileReader();
      reader.onloadend = () => {
        // Image preview handled in component state
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please select a valid image file');
    }
  };

  const handleRemoveResource = async (id) => {
    try {
      const resource = resources.find(r => r.id === id);
      
      // Delete from database
      const { error } = await supabase
        .from('resources')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // If it has an image file, also delete from storage
      if (resource.file_path) {
        await supabase.storage
          .from('design-submissions')
          .remove([resource.file_path]);
      }

      setResources(resources.filter(r => r.id !== id));
      if (onResourcesChange) onResourcesChange();
    } catch (error) {
      console.error('Error removing resource:', error);
      alert('Failed to remove resource. Please try again.');
    }
  };

  // Ensure we have exactly 3 cards (fill empty slots if needed)
  const displayResources = [...resources];
  while (displayResources.length < 3) {
    displayResources.push(null);
  }

  return (
    <div>
      <style>{`
        input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
        input[type="file"]::file-selector-button {
          background: rgba(215, 2, 246, 0.2);
          border: 1px solid #D702F6;
          color: #D702F6;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          margin-right: 1rem;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        input[type="file"]::file-selector-button:hover {
          background: rgba(215, 2, 246, 0.3);
        }
      `}</style>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '1rem' 
      }}>
        <h3 style={{ color: '#ffffff', margin: 0, fontSize: '1.25rem' }}>Resources & Reference Materials</h3>
        {isHost() && (
          <button 
            onClick={() => setIsEditing(!isEditing)} 
            style={{
              background: isEditing ? 'transparent' : 'transparent',
              border: `2px solid #D702F6`,
              color: '#D702F6',
              borderRadius: '8px',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              minWidth: '40px'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#D702F6';
              e.target.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#D702F6';
            }}
            title={isEditing ? 'Done Adding' : 'Add Resource'}
          >
            {isEditing ? '×' : '+'}
          </button>
        )}
      </div>

      {isHost() && isEditing && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: 'rgba(255, 255, 255, 0.9)', 
              marginBottom: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 500
            }}>
              Resource Title *
            </label>
            <input
              type="text"
              value={newResource.name}
              onChange={(e) => setNewResource(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter resource title..."
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#ffffff',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#D702F6';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: 'rgba(255, 255, 255, 0.9)', 
              marginBottom: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 500
            }}>
              Resource Type
            </label>
            <select
              value={newResource.type}
              onChange={(e) => {
                setNewResource(prev => ({ ...prev, type: e.target.value }));
                setUploadedImage(null);
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#ffffff',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#D702F6';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              <option value="link" style={{ background: '#1a1a1a', color: '#ffffff' }}>Link (with image)</option>
              <option value="file" style={{ background: '#1a1a1a', color: '#ffffff' }}>Image Only</option>
            </select>
          </div>

          {newResource.type === 'link' && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                color: 'rgba(255, 255, 255, 0.9)', 
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: 500
              }}>
                URL *
              </label>
              <input
                type="url"
                value={newResource.url}
                onChange={(e) => setNewResource(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://..."
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  color: '#ffffff',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#D702F6';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: 'rgba(255, 255, 255, 0.9)', 
              marginBottom: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 500
            }}>
              Upload Image *
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '2px dashed rgba(255, 255, 255, 0.3)',
                borderRadius: '6px',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                cursor: 'pointer'
              }}
            />
            {uploadedImage && (
              <div style={{ marginTop: '0.5rem' }}>
                <img 
                  src={URL.createObjectURL(uploadedImage)} 
                  alt="Preview" 
                  style={{
                    maxWidth: '200px',
                    maxHeight: '150px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                />
              </div>
            )}
          </div>

          <button 
            onClick={handleAddResource} 
            disabled={uploading || !newResource.name.trim() || (newResource.type === 'link' && !newResource.url.trim()) || !uploadedImage}
            style={{
              background: (uploading || !newResource.name.trim() || (newResource.type === 'link' && !newResource.url.trim()) || !uploadedImage) 
                ? 'rgba(215, 2, 246, 0.3)' 
                : '#D702F6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '0.875rem 2rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: (uploading || !newResource.name.trim() || (newResource.type === 'link' && !newResource.url.trim()) || !uploadedImage) 
                ? 'not-allowed' 
                : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: (uploading || !newResource.name.trim() || (newResource.type === 'link' && !newResource.url.trim()) || !uploadedImage) 
                ? 'none' 
                : '0 4px 15px rgba(215, 2, 246, 0.4)',
              opacity: (uploading || !newResource.name.trim() || (newResource.type === 'link' && !newResource.url.trim()) || !uploadedImage) 
                ? 0.6 
                : 1
            }}
            onMouseEnter={(e) => {
              if (!uploading && newResource.name.trim() && (newResource.type !== 'link' || newResource.url.trim()) && uploadedImage) {
                e.target.style.boxShadow = '0 6px 20px rgba(215, 2, 246, 0.6)';
              }
            }}
            onMouseLeave={(e) => {
              if (!uploading && newResource.name.trim() && (newResource.type !== 'link' || newResource.url.trim()) && uploadedImage) {
                e.target.style.boxShadow = '0 4px 15px rgba(215, 2, 246, 0.4)';
              }
            }}
          >
            {uploading ? 'Uploading...' : 'Add Resource'}
          </button>
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '1.5rem'
      }}>
        {displayResources.slice(0, 3).map((resource, index) => (
          <div 
            key={resource?.id || `empty-${index}`} 
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              cursor: resource ? 'pointer' : 'default'
            }}
            onMouseEnter={(e) => {
              if (resource) {
                e.currentTarget.style.borderColor = 'rgba(215, 2, 246, 0.5)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (resource) {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            {resource ? (
              <>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '56.25%', // 16:9 aspect ratio
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.05)'
                }}>
                  {(() => {
                    // For link resources: use file_path to generate image URL (url contains external link)
                    // For file resources: use url directly (url contains image URL)
                    // Check if we have imageUrl from App.js loadResources, otherwise generate it
                    let imageUrl = resource.imageUrl || null;
                    
                    if (!imageUrl) {
                      if (resource.type === 'link' && resource.file_path) {
                        const { data: { publicUrl } } = supabase.storage
                          .from('design-submissions')
                          .getPublicUrl(resource.file_path);
                        imageUrl = publicUrl;
                      } else if (resource.type === 'file' && resource.url) {
                        // For file resources, url is the image URL
                        imageUrl = resource.url;
                      }
                    }
                    
                    return imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={resource.name}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', imageUrl);
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', imageUrl);
                        }}
                      />
                    ) : null;
                  })()}
                  <div 
                    style={{ 
                      display: (resource.url || resource.file_path) ? 'none' : 'flex',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '0.875rem'
                    }}
                  >
                    <span>No Image</span>
                  </div>
                  {isHost() && isEditing && (
                    <button 
                      onClick={() => handleRemoveResource(resource.id)}
                      title="Remove resource"
                      style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        background: 'rgba(0, 0, 0, 0.7)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        color: '#ffffff',
                        fontSize: '1.25rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(215, 2, 246, 0.8)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(0, 0, 0, 0.7)';
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
                <div style={{
                  padding: '1rem'
                }}>
                  <h3 style={{
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    marginTop: 0
                  }}>
                    {resource.name}
                  </h3>
                  {resource.type === 'link' && (resource.externalUrl || resource.url) && (
                    <a 
                      href={resource.externalUrl || resource.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        // Prevent React Router from intercepting the link
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const url = resource.externalUrl || resource.url;
                        if (!url) return;
                        
                        // Ensure URL has protocol
                        let finalUrl = url;
                        if (!url.startsWith('http://') && !url.startsWith('https://')) {
                          finalUrl = `https://${url}`;
                        }
                        
                        // Open in new tab
                        window.open(finalUrl, '_blank', 'noopener,noreferrer');
                      }}
                      style={{
                        color: '#D702F6',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        transition: 'color 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = 'rgba(215, 2, 246, 0.8)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#D702F6';
                      }}
                    >
                      View Resource →
                    </a>
                  )}
                </div>
              </>
            ) : (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: 'rgba(255, 255, 255, 0.5)',
                minHeight: '200px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: 500,
                  marginBottom: '0.5rem'
                }}>
                  Empty Slot
                </span>
                <p style={{ 
                  fontSize: '0.75rem', 
                  margin: 0,
                  color: 'rgba(255, 255, 255, 0.4)'
                }}>
                  Add a resource to fill this card
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ResourceSection;
